<script setup>
/**
 * 自定義對話框組件
 * @vue/component
 * @component NewCusDialog
 *
 * @example
 * <NewCusDialog
 *   v-model="dialogVisible"
 *   title="對話框標題"
 *   width="400"
 *   height="auto"
 *   variant="dialog"
 *   type="info"
 *   :hideClose="false"
 *   :closeOnOverlay="true"
 * >
 *   <template #header>自定義頭部內容</template>
 *   <template #body>對話框內容</template>
 *   <template #footer>底部按鈕</template>
 * </NewCusDialog>
 *
 * @model {Boolean} visible - 控制對話框顯示/隱藏的雙向綁定值 (使用 defineModel)
 *
 * @prop {String} title - 對話框標題
 * @prop {String|Number} width - 對話框寬度,可以是數字(px)或CSS寬度值,預設'400'
 * @prop {String|Number} height - 對話框高度,可以是數字(px)或CSS高度值,預設'auto'
 * @prop {String} variant - 對話框類型,可選:'dialog'|'alert',預設'dialog'
 * @prop {String} type - alert類型的對話框圖標樣式,可選:'info',預設'info'
 * @prop {Boolean} hideClose - 是否隱藏關閉按鈕,預設false
 * @prop {Boolean} closeOnOverlay - 點擊遮罩層是否關閉對話框,預設true
 *
 * @slot header - 自定義頭部內容,預設顯示標題
 * @slot body - 主要內容區域
 * @slot footer - 底部按鈕區域
 */
import { computed } from 'vue';
defineOptions({
  name: 'new-cus-dialog',
});

const visible = defineModel('visible', { type: Boolean });

const props = defineProps({
  title: { type: String, default: '' },
  width: { type: [String, Number], default: '400' },
  height: { type: [String, Number], default: 'auto' },
  variant: { type: String, default: 'dialog' },
  type: { type: String, default: 'info' },
  hideClose: { type: Boolean, default: false },
  closeOnOverlay: { type: Boolean, default: true },
});

const computedStyle = computed(() => ({
  width: isNaN(Number(props.width)) ? props.width : `${props.width}px`,
  height: isNaN(Number(props.height)) ? props.height : `${props.height}px`,
}));

const emit = defineEmits(['close']);

const handleClose = () => {
  visible.value = false;
  // 發射close事件，父組件可以監聽該事件
  emit('close');
};
</script>

<template>
  <Teleport to="#dialog">
    <transition name="fade">
      <div v-if="visible" class="dialog-overlay" @click.self="props.closeOnOverlay && handleClose()">
        <div class="dialog-box" :class="{ alert: !$slots.footer }" :style="computedStyle">
          <div class="dialog-header">
            <slot name="header">
              <template v-if="props.variant === 'alert'">
                <div class="d-flex alert-header">
                  <template v-if="props.type === 'info'">
                    <img src="../assets/images/icon-notice.png" alt="" class="dialog-title-image" />
                  </template>
                  <p v-if="title" class="dialog-title">{{ title }}</p>
                </div>
              </template>
              <template v-else>
                <div class="d-flex dialog-header-centered">
                  <p v-if="title" class="dialog-title">{{ title }}</p>
                </div>
              </template>
            </slot>

            <div v-if="!props.hideClose" class="dialog-cancel" @click="handleClose">
              <i class="bi bi-x-circle-fill"></i>
            </div>
          </div>
          <div class="dialog-body">
            <slot name="body"></slot>
          </div>
          <div v-if="$slots.footer" class="dialog-footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>
<style lang="scss" scoped>
* {
  // outline: 1px solid #000;
}
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: $color-mask;
  z-index: 900;
}

.dialog-box {
  position: relative;
  display: grid;
  grid-template-rows: 50px 1fr 60px; // 固定 header、body 及 footer 高度
  grid-row-gap: 10px;
  max-width: 1200px;
  height: 100%;
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  &.alert {
    grid-template-rows: 50px 1fr;
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.dialog-body {
  overflow: auto;
}

.dialog-title {
  font-size: 2.4rem;
  font-weight: 500;
  color: $color-blue;
}

.dialog-cancel {
  position: absolute;
  top: 4px;
  right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  color: #b3b3b3;
  transition: 0.1s ease;
  i {
    font-size: 2.4rem;
  }
  &:active {
    background-color: #eeeeee;
    color: #4e4e4e;
    transition: 0.1s ease;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dialog-header-centered {
  width: 100%;
  justify-content: center;
  text-align: center;
}

.alert-header {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
}

.dialog-title-image {
  width: 32px;
  height: 32px;
  margin-right: 5px;
}
</style>
